import React, { useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import { alpha, keyframes, useTheme } from "@mui/material/styles";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import ChildCareOutlinedIcon from "@mui/icons-material/ChildCareOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import MDButton from "components/MDButton";

const STORAGE_KEY = "askuala_staff_policy_accepted_v1";

export function useStaffPolicyModal() {
  const accepted = () => localStorage.getItem(STORAGE_KEY) === "true";
  const accept = () => localStorage.setItem(STORAGE_KEY, "true");
  return { accepted, accept };
}

const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(26,115,232,0.35); }
  70%  { box-shadow: 0 0 0 10px rgba(26,115,232,0); }
  100% { box-shadow: 0 0 0 0 rgba(26,115,232,0); }
`;

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(4px); }
`;

const LINK_BLUE = "#1A73E8";
const LINK_BLUE_HOVER = "#0b5bbd";

function ResponsibilityItem({ icon, children }) {
  const theme = useTheme();
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Box
        sx={{
          mt: "2px",
          width: 28,
          height: 28,
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: alpha(theme.palette.info.main, 0.12),
          color: theme.palette.info.main,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography variant="body2" sx={{ lineHeight: 1.6, color: theme.palette.text.primary }}>
        {children}
      </Typography>
    </Stack>
  );
}

ResponsibilityItem.propTypes = {
  icon: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
};

export default function PolicyAcknowledgeModal({ open, onAccept }) {
  const theme = useTheme();
  const [checked, setChecked] = useState(false);

  const toggle = () => setChecked((c) => !c);

  const linkSx = {
    color: LINK_BLUE,
    textDecoration: "underline",
    textUnderlineOffset: "3px",
    textDecorationThickness: "1.5px",
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
    transition: "color 0.15s ease",
    "&:hover": { color: LINK_BLUE_HOVER, textDecorationThickness: "2px" },
    "&:focus-visible": {
      outline: `2px solid ${LINK_BLUE}`,
      outlineOffset: "2px",
      borderRadius: "2px",
    },
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
        },
      }}
    >
      {/* Gradient Header */}
      <Box
        sx={{
          position: "relative",
          px: 3,
          pt: 3,
          pb: 2.5,
          background: `linear-gradient(135deg, ${LINK_BLUE} 0%, #42a5f5 100%)`,
          color: "#fff",
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              color: "#fff",
              width: 48,
              height: 48,
              backdropFilter: "blur(8px)",
            }}
          >
            <ShieldOutlinedIcon />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "#fff", lineHeight: 1.2, letterSpacing: "-0.2px" }}
            >
              Educator &amp; Administrator
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.9)", mt: 0.25, fontWeight: 500 }}
            >
              Data Responsibilities Agreement
            </Typography>
          </Box>
        </Stack>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5, background: theme.palette.background.default }}>
        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.primary, lineHeight: 1.65, mb: 2 }}
        >
          Welcome to the <b>Askuala Link Smart School System</b>. As an authorized staff member, you
          have access to official student records, live attendance tracking, and examination
          management tools.
        </Typography>

        <Typography
          variant="caption"
          sx={{
            display: "block",
            textTransform: "uppercase",
            letterSpacing: "0.8px",
            fontWeight: 700,
            color: theme.palette.text.secondary,
            mb: 1.25,
          }}
        >
          By accessing this system, you agree to:
        </Typography>

        <Stack spacing={1.5} sx={{ mb: 2.5 }}>
          <ResponsibilityItem icon={<LockOutlinedIcon sx={{ fontSize: 16 }} />}>
            Maintain strict confidentiality of student academic and personal information.
          </ResponsibilityItem>
          <ResponsibilityItem icon={<FactCheckOutlinedIcon sx={{ fontSize: 16 }} />}>
            Ensure accurate evaluation when utilizing automated grading and diagnostic features.
          </ResponsibilityItem>
          <ResponsibilityItem icon={<ChildCareOutlinedIcon sx={{ fontSize: 16 }} />}>
            Adhere to Askuala Link&apos;s data security guidelines and child safeguarding standards.
          </ResponsibilityItem>
        </Stack>

        {/* Policy links callout */}
        <Box
          sx={{
            p: 1.5,
            mb: 2.5,
            borderRadius: "10px",
            background: alpha(LINK_BLUE, 0.06),
            border: `1px solid ${alpha(LINK_BLUE, 0.18)}`,
          }}
        >
          <Typography variant="body2" sx={{ color: theme.palette.text.primary, lineHeight: 1.6 }}>
            Please review our full policies:{" "}
            <Link
              href="https://askualalink.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              sx={linkSx}
            >
              Privacy Policy
              <OpenInNewRoundedIcon sx={{ fontSize: 14 }} />
            </Link>{" "}
            and{" "}
            <Link
              href="https://askualalink.com/safeguarding"
              target="_blank"
              rel="noopener noreferrer"
              sx={linkSx}
            >
              Child Safeguarding Policy
              <OpenInNewRoundedIcon sx={{ fontSize: 14 }} />
            </Link>
            .
          </Typography>
        </Box>

        {/* Attention pointer (only when unchecked) */}
        {!checked && (
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            justifyContent="center"
            sx={{ mb: 1, color: LINK_BLUE }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: "0.3px" }}>
              Tick the box below to continue
            </Typography>
            <ArrowDownwardRoundedIcon
              sx={{ fontSize: 16, animation: `${bounce} 1.4s ease-in-out infinite` }}
            />
          </Stack>
        )}

        {/* Clickable acknowledgement card */}
        <Box
          role="checkbox"
          tabIndex={0}
          aria-checked={checked}
          aria-label="I have read and agree to the policies"
          onClick={toggle}
          onKeyDown={(e) => {
            if (e.key === " " || e.key === "Enter") {
              e.preventDefault();
              toggle();
            }
          }}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            p: 1.75,
            borderRadius: "12px",
            cursor: "pointer",
            userSelect: "none",
            border: `2px solid ${checked ? theme.palette.success.main : alpha(LINK_BLUE, 0.35)}`,
            background: checked ? alpha(theme.palette.success.main, 0.08) : alpha(LINK_BLUE, 0.04),
            transition: "all 0.2s ease",
            animation: checked ? "none" : `${pulse} 2s ease-in-out infinite`,
            "&:hover": {
              background: checked
                ? alpha(theme.palette.success.main, 0.12)
                : alpha(LINK_BLUE, 0.08),
              borderColor: checked ? theme.palette.success.main : LINK_BLUE,
            },
            "&:focus-visible": {
              outline: `3px solid ${alpha(LINK_BLUE, 0.4)}`,
              outlineOffset: "2px",
            },
          }}
        >
          <Checkbox
            checked={checked}
            onChange={toggle}
            onClick={(e) => e.stopPropagation()}
            icon={<Box sx={{ width: 20, height: 20 }} />}
            sx={{
              p: 0,
              mt: "2px",
              color: alpha(LINK_BLUE, 0.6),
              "&.Mui-checked": { color: theme.palette.success.main },
            }}
            inputProps={{ "aria-label": "Agreement acknowledgement" }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: checked ? theme.palette.success.dark : theme.palette.text.primary,
                lineHeight: 1.5,
              }}
            >
              {checked ? (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <TaskAltRoundedIcon sx={{ fontSize: 18 }} />
                  <span>Acknowledged — you may continue</span>
                </Stack>
              ) : (
                "I have read and agree to the policies above."
              )}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary, display: "block", mt: 0.5 }}
            >
              I acknowledge my responsibility to protect student and minor data.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, background: theme.palette.background.default }}>
        <MDButton
          variant="gradient"
          color="info"
          disabled={!checked}
          onClick={onAccept}
          fullWidth
          sx={{
            height: 46,
            fontWeight: 700,
            letterSpacing: "0.5px",
            borderRadius: "12px",
            boxShadow: checked ? "0 8px 20px rgba(26,115,232,0.35)" : "none",
            transition: "all 0.25s ease",
          }}
        >
          I Acknowledge &amp; Accept
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

PolicyAcknowledgeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onAccept: PropTypes.func.isRequired,
};
